import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router";
import Moment from "react-moment";
import { List as RWList } from "react-window";
import Loading from "../Common/Loading";

const STORAGE_KEY = "waylens.eventList.filters.v4";
const EVENT_OPTIONS = ["", "AGMT_Piezo", "AGMT_Mic", "AGMT_MicPiezo"];
const ORDER_BY = {
    RECEIVED_DATE_TIME: "ReceivedDateTime",
    EVENT_DATE_TIME: "EventDateTime",
};
const ORDER_BY_OPTIONS = [
    { value: ORDER_BY.RECEIVED_DATE_TIME, label: "Received" },
    { value: ORDER_BY.EVENT_DATE_TIME, label: "Event" },
];
const PAIRING_STATUS_OPTIONS = [
    { value: "", label: "All" },
    { value: "Match", label: "Match" },
    { value: "Mismatch", label: "Mismatch" },
    { value: "Unknown", label: "Unknown" },
];

const ROW_HEIGHT = 64;
const OVERSCAN = 8;
const TABLE_GRID_TEMPLATE = [
    "60px",
    "minmax(120px, 1fr)",
    "minmax(150px, 1.15fr)",
    "minmax(155px, 1.1fr)",
    "68px",
    "125px",
    "90px",
    "minmax(174px, 1.25fr)",
    "minmax(174px, 1.25fr)",
].join(" ");
const TABLE_MIN_WIDTH = 1140;

const THEME = {
    pageBg: "#0b0f14",
    panelBg: "#0e141c",
    headerBg: "#0b0f14",
    border: "rgba(255,255,255,0.12)",
    rowA: "rgba(255,255,255,0.02)",
    rowB: "rgba(255,255,255,0.00)",
    hover: "#094f7e",
    matchRow: "rgba(46,160,67,0.14)",
    matchHover: "rgba(46,160,67,0.25)",
    matchAccent: "#3fb950",
    mismatchRow: "rgba(248,81,73,0.14)",
    mismatchHover: "rgba(248,81,73,0.25)",
    mismatchAccent: "#f85149",
    text: "#e6edf3",
    subText: "rgba(230,237,243,0.72)",
};

function safeJsonParse(str) {
    try {
        return JSON.parse(str);
    } catch {
        return null;
    }
}

function normalizeDraft(draft) {
    const trimmedId = (draft.id ?? "").trim();
    const parsedId = Number(trimmedId);

    const icxMacAddress = (draft.icxMacAddress ?? "").trim();

    const rawIcxEvent = (draft.eventIcx ?? "").trim();
    const pairingStatus = PAIRING_STATUS_OPTIONS.some((option) => option.value === draft.pairingStatus)
        ? draft.pairingStatus
        : "";

    let eventIcx = "";
    if (rawIcxEvent.length > 0) {
        const n = Number.parseInt(rawIcxEvent, 10);
        if (Number.isInteger(n) && n >= 0 && n <= 255) {
            eventIcx = String(n);
        }
    }

    return {
        id: Number.isFinite(parsedId) && parsedId > 0 ? String(parsedId) : "",
        deviceSerialNumber: (draft.deviceSerialNumber ?? "").trim(),
        event: (draft.event ?? "").trim(),
        icxMacAddress,
        eventIcx,
        pairingStatus,
        orderBy: draft.orderBy === ORDER_BY.EVENT_DATE_TIME
            ? ORDER_BY.EVENT_DATE_TIME
            : ORDER_BY.RECEIVED_DATE_TIME,
    };
}


function DateCell({ value }) {
    if (!value) return <span style={{ color: THEME.subText }}>-</span>;

    return (
        <div style={{ lineHeight: 1.15 }}>
            <div style={{ color: THEME.text, fontWeight: 600 }}>
                <Moment fromNow>{value}</Moment>
            </div>
            <div style={{ fontSize: "0.82rem", color: THEME.subText }}>
                <Moment format="YYYY-MM-DD HH:mm:ss">{value}</Moment>
            </div>
        </div>
    );
}

function TextCell({ value }) {
    if (value === null || value === undefined || value === "")
        return <span style={{ color: THEME.subText }}>-</span>;
    return <span>{value}</span>;
}

function PairingStatusBadge({ isMatch }) {
    const status = isMatch === true
        ? { label: "Match", bg: "success" }
        : isMatch === false
            ? { label: "Mismatch", bg: "danger" }
            : { label: "Unknown", bg: "secondary" };

    return (
        <Badge bg={status.bg} style={{ fontSize: "0.80rem", fontWeight: 700 }}>
            {status.label}
        </Badge>
    );
}

function buildQueryString({ appliedFilters, pageNumber, pageSize }) {
    const params = new URLSearchParams();

    params.set("PagingRequest.PageNumber", String(pageNumber));
    params.set("PagingRequest.PageSize", String(pageSize));
    params.set("OrderBy", appliedFilters.orderBy ?? ORDER_BY.RECEIVED_DATE_TIME);

    if (appliedFilters.id) params.set("Id", appliedFilters.id);
    if (appliedFilters.deviceSerialNumber) params.set("DeviceSerialNumber", appliedFilters.deviceSerialNumber);
    if (appliedFilters.event) params.set("Event", appliedFilters.event);
    if (appliedFilters.icxMacAddress) params.set("IcxMacAddress", appliedFilters.icxMacAddress);
    if (appliedFilters.eventIcx) params.set("EventIcx", appliedFilters.eventIcx);
    if (appliedFilters.pairingStatus) params.set("PairingStatus", appliedFilters.pairingStatus);

    return params.toString();
}

function WaylensRowComponent(props) {
    const { index, style, rows, hasMore, isLoadingMore, onSelect } = props;

    if (index >= rows.length) {
        return (
            <div
                style={{
                    ...style,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 12px",
                    boxSizing: "border-box",
                    minWidth: TABLE_MIN_WIDTH,
                    borderBottom: `1px solid ${THEME.border}`,
                    background: THEME.panelBg,
                    color: THEME.subText,
                }}
            >
                {hasMore ? (
                    <span>{isLoadingMore ? "Loading more…" : "Scroll to load more…"}</span>
                ) : (
                    <span>End of results.</span>
                )}
            </div>
        );
    }

    const row = rows[index];
    const defaultBg = index % 2 === 0 ? THEME.rowA : THEME.rowB;
    const baseBg = row.isIcxCameraPairMatch === true
        ? THEME.matchRow
        : row.isIcxCameraPairMatch === false
            ? THEME.mismatchRow
            : defaultBg;
    const hoverBg = row.isIcxCameraPairMatch === true
        ? THEME.matchHover
        : row.isIcxCameraPairMatch === false
            ? THEME.mismatchHover
            : THEME.hover;
    const statusAccent = row.isIcxCameraPairMatch === true
        ? THEME.matchAccent
        : row.isIcxCameraPairMatch === false
            ? THEME.mismatchAccent
            : null;

    return (
        <div
            style={{
                ...style,
                display: "grid",
                gridTemplateColumns: TABLE_GRID_TEMPLATE,
                alignItems: "center",
                padding: "0 12px",
                boxSizing: "border-box",
                minWidth: TABLE_MIN_WIDTH,
                cursor: "pointer",
                borderBottom: `1px solid ${THEME.border}`,
                backgroundColor: baseBg,
                boxShadow: statusAccent ? `inset 3px 0 0 ${statusAccent}` : "none",
                color: THEME.text,
                fontSize: "0.94rem",
            }}
            onClick={() => onSelect(row)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onSelect(row);
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = baseBg)}
        >
            <div style={{ fontWeight: 700 }}>{row.id}</div>

            <div className="text-truncate pe-3" title={row.deviceSerialNumber ?? undefined}>
                {row.deviceSerialNumber ? row.deviceSerialNumber : <span style={{ color: THEME.subText }}>-</span>}
            </div>

            <div className="text-truncate pe-3" title={row.event ?? undefined}>
                {row.event ? (
                    <>
                        {row.event}
                        {row.eventIcx !== null && row.eventIcx !== undefined ? ` (${row.eventIcx})` : ""}
                    </>
                ) : (
                    <TextCell
                        value={row.eventIcx !== null && row.eventIcx !== undefined ? `(${row.eventIcx})` : null}
                    />
                )}
            </div>

            <div className="text-truncate pe-3" title={row.icxMacAddress ?? undefined}>
                <TextCell value={row.icxMacAddress} />
            </div>

            <div className="text-truncate pe-3">
                <TextCell value={row.icxEventTypeCount} />
            </div>

            <div className="text-truncate pe-3">
                <TextCell value={row.icxSecondsSinceEventDetected} />
            </div>

            <div className="pe-2">
                <PairingStatusBadge isMatch={row.isIcxCameraPairMatch} />
            </div>

            <div className="pe-3">
                <DateCell value={row.eventTimeUtc} />
            </div>

            <div>
                <DateCell value={row.receivedUtc} />
            </div>
        </div>
    );
}

export const WaylensEventList = ({ apiFetch }) => {
    document.title = "AGMT | Waylens Events";

    const navigate = useNavigate();

    const initial = useMemo(() => {
        const stored = safeJsonParse(localStorage.getItem(STORAGE_KEY));
        const fallback = {
            id: "",
            deviceSerialNumber: "",
            event: "",
            icxMacAddress: "",
            eventIcx: "",
            pairingStatus: "",
            orderBy: ORDER_BY.RECEIVED_DATE_TIME,
        };
        return normalizeDraft(stored ? { ...fallback, ...stored } : fallback);
    }, []);

    const [draft, setDraft] = useState(initial);
    const [applied, setApplied] = useState(normalizeDraft(initial));

    const [rows, setRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(100);

    const [hasMore, setHasMore] = useState(true);
    const [fetchingFirstPage, setFetchingFirstPage] = useState(false);
    const [fetchingMore, setFetchingMore] = useState(false);
    const [error, setError] = useState("");

    const [listHeight, setListHeight] = useState(600);

    useEffect(() => {
        const recompute = () => {
            const px = Math.max(280, window.innerHeight - 300);
            setListHeight(px);
        };
        recompute();
        window.addEventListener("resize", recompute);
        return () => window.removeEventListener("resize", recompute);
    }, []);

    const inFlightRef = useRef(false);

    const activeFilters = useMemo(() => {
        const items = [];
        if (applied.id) items.push({ k: "Id", v: applied.id });
        if (applied.deviceSerialNumber) items.push({ k: "Serial", v: applied.deviceSerialNumber });
        if (applied.event) items.push({ k: "Event", v: applied.event });
        if (applied.icxMacAddress) items.push({ k: "ICx MAC", v: applied.icxMacAddress });
        if (applied.eventIcx) items.push({ k: "ICx Event", v: applied.eventIcx });
        if (applied.pairingStatus) items.push({ k: "Pairing", v: applied.pairingStatus });

        return items;
    }, [applied]);


    const persistApplied = useCallback((filters) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }, []);

    const fetchPage = useCallback(
        async ({ nextPageNumber, mode }) => {
            if (inFlightRef.current) return;
            inFlightRef.current = true;

            setError("");

            try {
                const query = buildQueryString({
                    appliedFilters: applied,
                    pageNumber: nextPageNumber,
                    pageSize,
                });

                const url = `/api/Waylens/GetWaylensEvents?${query}`;
                const resp = await apiFetch(url);

                if (!resp.ok) {
                    setError(`Failed to load Waylens events. Status ${resp.status}`);
                    return;
                }

                const payload = await resp.json();
                const newRows = payload?.results ?? [];
                const more = payload?.pageDetail?.hasMoreData ?? false;

                if (mode === "replace") setRows(newRows);
                else setRows((prev) => [...prev, ...newRows]);

                setHasMore(Boolean(more));
                setPageNumber(nextPageNumber);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                setError("Failed to load events due to a network or server error.");
            } finally {
                inFlightRef.current = false;
                setFetchingFirstPage(false);
                setFetchingMore(false);
            }
        },
        [apiFetch, applied, pageSize]
    );

    useEffect(() => {
        setRows([]);
        setHasMore(true);
        setPageNumber(1);

        setFetchingFirstPage(true);
        fetchPage({ nextPageNumber: 1, mode: "replace" });
    }, [applied, fetchPage]);

    const applyFilters = useCallback(() => {
        const normalized = normalizeDraft(draft);
        setApplied(normalized);
        persistApplied(normalized);
    }, [draft, persistApplied]);

    const clearFilters = useCallback(() => {
        const cleared = {
            id: "",
            deviceSerialNumber: "",
            event: "",
            icxMacAddress: "",
            eventIcx: "",
            pairingStatus: "",
            orderBy: applied.orderBy ?? ORDER_BY.RECEIVED_DATE_TIME,
        };
        setDraft(cleared);
        setApplied(cleared);
        persistApplied(cleared);
    }, [applied.orderBy, persistApplied]);

    const changeOrderBy = useCallback((orderBy) => {
        const normalizedOrderBy = orderBy === ORDER_BY.EVENT_DATE_TIME
            ? ORDER_BY.EVENT_DATE_TIME
            : ORDER_BY.RECEIVED_DATE_TIME;

        setDraft((previousDraft) => ({
            ...previousDraft,
            orderBy: normalizedOrderBy,
        }));

        setApplied((previousApplied) => {
            const nextApplied = {
                ...previousApplied,
                orderBy: normalizedOrderBy,
            };
            persistApplied(nextApplied);
            return nextApplied;
        });
    }, [persistApplied]);

    const onSelectRow = useCallback(
        (row) => navigate(`/waylens/event/${row.id}`),
        [navigate]
    );

    const onRowsRendered = useCallback(
        (visibleRows) => {
            if (!hasMore || fetchingMore || fetchingFirstPage) return;

            const stopIndex = visibleRows?.stopIndex ?? 0;
            const threshold = rows.length - 1 - 8;

            if (stopIndex >= threshold && rows.length > 0) {
                setFetchingMore(true);
                fetchPage({ nextPageNumber: pageNumber + 1, mode: "append" });
            }
        },
        [hasMore, fetchingMore, fetchingFirstPage, rows.length, fetchPage, pageNumber]
    );

    const rowCount = useMemo(() => rows.length + (hasMore ? 1 : 0), [rows.length, hasMore]);

    const panelStyle = {
        background: THEME.panelBg,
        border: `1px solid ${THEME.border}`,
        borderRadius: 8,
    };

    return (
        <div className="webcontainer" style={{ background: THEME.pageBg, minHeight: "100vh" }}>
            <Container fluid>
                <Row className="mb-2">
                    <Col md={12} className="fixed-col2">
                        <div className="contentvehicle3">
                            <h4 className="text-light mb-0">Waylens Events</h4>
                        </div>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={12}>
                        <div className="p-3" style={panelStyle}>
                            <Form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    applyFilters();
                                }}
                            >
                                <Row className="g-2 align-items-end">
                                    <Col xs={12} md={2}>
                                        <Form.Label className="text-light">Event Id</Form.Label>
                                        <Form.Control
                                            value={draft.id}
                                            onChange={(e) => setDraft((p) => ({...p, id: e.target.value}))}
                                            placeholder="e.g. 12345"
                                            className="bg-dark text-light"
                                            style={{borderColor: THEME.border}}
                                        />
                                    </Col>

                                    <Col xs={12} md={2}>
                                        <Form.Label className="text-light">Camera SN</Form.Label>
                                        <Form.Control
                                            value={draft.deviceSerialNumber}
                                            onChange={(e) => setDraft((p) => ({
                                                ...p,
                                                deviceSerialNumber: e.target.value
                                            }))}
                                            placeholder="Exact serial"
                                            className="bg-dark text-light"
                                            style={{borderColor: THEME.border}}
                                        />
                                    </Col>

                                    <Col xs={12} md={2}>
                                        <Form.Label className="text-light">Event Type</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={draft.event}
                                            onChange={(e) => setDraft((p) => ({...p, event: e.target.value}))}
                                            className="bg-dark text-light"
                                            style={{borderColor: THEME.border}}
                                        >
                                            {EVENT_OPTIONS.map((opt) => (
                                                <option key={opt} value={opt}>
                                                    {opt === "" ? "All" : opt}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Col>

                                    <Col xs={12} md={2}>
                                        <Form.Label className="text-light">ICx MAC Address</Form.Label>
                                        <Form.Control
                                            value={draft.icxMacAddress}
                                            onChange={(e) => setDraft((p) => ({ ...p, icxMacAddress: e.target.value }))}
                                            placeholder="Exact MAC (e.g. AA:BB:CC:DD:EE:FF)"
                                            className="bg-dark text-light"
                                            style={{ borderColor: THEME.border }}
                                        />
                                    </Col>

                                    <Col xs={12} md={2}>
                                        <Form.Label className="text-light">ICx Event Type</Form.Label>
                                        <Form.Control
                                            value={draft.eventIcx}
                                            onChange={(e) => setDraft((p) => ({ ...p, eventIcx: e.target.value }))}
                                            placeholder="7-9"
                                            className="bg-dark text-light"
                                            style={{ borderColor: THEME.border }}
                                            inputMode="numeric"
                                        />
                                    </Col>

                                    <Col xs={12} md={2}>
                                        <Form.Label className="text-light">Pairing Status</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={draft.pairingStatus}
                                            onChange={(e) => setDraft((p) => ({ ...p, pairingStatus: e.target.value }))}
                                            className="bg-dark text-light"
                                            style={{ borderColor: THEME.border }}
                                        >
                                            {PAIRING_STATUS_OPTIONS.map((option) => (
                                                <option key={option.value || "all"} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Col>

                                    <Col xs={12} md={2}>
                                        <Form.Label className="text-light">Order By</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={draft.orderBy}
                                            onChange={(e) => changeOrderBy(e.target.value)}
                                            className="bg-dark text-light"
                                            style={{ borderColor: THEME.border }}
                                        >
                                            {ORDER_BY_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Col>

                                    <Col xs={12} md="auto" className="d-flex gap-2">
                                        <Button variant="info" onClick={applyFilters}>
                                            Search
                                        </Button>
                                        <Button variant="outline-light" onClick={clearFilters}>
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>

                            <div
                                className="mt-3 d-flex flex-wrap align-items-center gap-2"
                                style={{color: THEME.text}}
                            >
                                {activeFilters.length > 0 ? (
                                    <>
      <span style={{color: THEME.text, fontWeight: 600}}>
        Filters applied:
      </span>

                                        {activeFilters.map((f) => (
                                            <span
                                                key={f.k}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    padding: "4px 10px",
                                                    borderRadius: 999,
                                                    border: `1px solid ${THEME.border}`,
                                                    background: "#111a24",
                                                    color: THEME.text,
                                                    fontWeight: 600,
                                                    fontSize: "0.85rem",
                                                    lineHeight: 1.2,
                                                }}
                                            >
          {f.k}: {f.v}
        </span>
                                        ))}
                                    </>
                                ) : (
                                    <span style={{color: THEME.text, fontWeight: 600}}>
      No filters applied.
    </span>
                                )}
                            </div>

                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col md={12}>
                        {fetchingFirstPage && <Loading />}

                        {!fetchingFirstPage && error && <div className="alert alert-danger mb-3">{error}</div>}

                        {!fetchingFirstPage && !error && (
                            <div style={{ ...panelStyle, overflowX: "auto", overflowY: "hidden" }}>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: TABLE_GRID_TEMPLATE,
                                        minWidth: TABLE_MIN_WIDTH,
                                        width: "100%",
                                        boxSizing: "border-box",
                                        alignItems: "center",
                                        padding: "10px 12px",
                                        borderBottom: `1px solid ${THEME.border}`,
                                        background: THEME.headerBg,
                                        color: THEME.text,
                                        fontSize: "0.88rem",
                                        fontWeight: 600,
                                    }}
                                >
                                    <div>Id</div>
                                    <div className="pe-3">
                                        Camera SN
                                    </div>
                                    <div className="pe-3">
                                        Event Type
                                    </div>
                                    <div className="pe-3">ICX MAC</div>
                                    <div className="pe-3">ICX Count</div>
                                    <div className="pe-3">Seconds Since Crash (s)</div>
                                    <div className="pe-2">Pairing</div>
                                    <div className="pe-3">
                                        Event Time (UTC)
                                    </div>
                                    <div>Received (UTC)</div>
                                </div>

                                {rows.length === 0 ? (
                                    <div className="p-4" style={{ color: THEME.text }}>
                                        No results found.
                                    </div>
                                ) : (
                                    <RWList
                                        rowComponent={WaylensRowComponent}
                                        rowCount={rowCount}
                                        rowHeight={ROW_HEIGHT}
                                        rowProps={{
                                            rows,
                                            hasMore,
                                            isLoadingMore: fetchingMore,
                                            onSelect: onSelectRow,
                                        }}
                                        overscanCount={OVERSCAN}
                                        onRowsRendered={onRowsRendered}
                                        style={{ height: listHeight, minWidth: TABLE_MIN_WIDTH, width: "100%" }}
                                    />
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};
